import { useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { BatchDetailLink, ShipmentDetailLink } from "@/components/hub/HubEntityDetailLink";
import type { BatchPeekContext } from "@/components/hub/entityPeekTypes";
import { useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { HoverHintContent } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { buildMrpSkuDetailPath, type MrpSkuHoverHintFields } from "@/lib/mrp";
import { cn } from "@/lib/utils";

import {
  compareSuntronicInvoiceShipments,
  type SuntronicShipmentSortKey,
} from "./suntronicInvoiceShipmentsSearch";
import type { SuntronicInvoiceShipmentRow } from "./suntronicInvoicesApi";
import { formatQuantity } from "./suntronicInvoicesFormat";
import {
  resolveSuntronicShipmentLocationId,
  SUNTRONIC_SHIPMENTS_MATCHED_QTY_HOVER_HINT,
  SUNTRONIC_SHIPMENTS_REMAINING_QTY_HOVER_HINT,
} from "./suntronicInvoicesShipmentsColumns";

/** Match HubEntityDetailLink / shipping SKU triggers, without underline. */
const ENTITY_LINK_CLASS =
  "font-medium text-sky-700 no-underline hover:text-sky-900 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";

type HubBatchPeekApi = {
  batch_id: number;
  mfr_ship_by_date: string | null;
  ship_to_label: string | null;
  location_id?: number | null;
  location_label?: string | null;
  shipment_on_hold: boolean;
  serial_count: number;
  line_count: number;
  work_order_numbers: string[];
  finished_sku_lines: { sku: string; quantity: number }[];
};

function batchPeekFromApi(
  apiPeek: HubBatchPeekApi | undefined,
  loading: boolean,
): BatchPeekContext | undefined {
  if (apiPeek == null && !loading) return undefined;
  return {
    mfr_ship_by_date: apiPeek?.mfr_ship_by_date ?? null,
    ship_to_label: apiPeek?.ship_to_label ?? null,
    location_id: apiPeek?.location_id ?? null,
    location_label: apiPeek?.location_label ?? null,
    shipment_on_hold: apiPeek?.shipment_on_hold ?? false,
    line_count: apiPeek?.line_count ?? null,
    serial_count: apiPeek?.serial_count ?? null,
    work_order_numbers: apiPeek?.work_order_numbers,
    finished_sku_lines: apiPeek?.finished_sku_lines,
    loading: loading && apiPeek == null,
  };
}

function lookupBatchPeek(
  peeks: Record<number, HubBatchPeekApi> | undefined,
  batchId: number,
): HubBatchPeekApi | undefined {
  if (peeks == null) return undefined;
  return peeks[batchId];
}

function formatDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Date(parsed).toLocaleDateString();
}

function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 text-slate-900", className)}>{children}</td>;
}

function HeaderHintLabel({
  label,
  hoverHint,
}: {
  label: string;
  hoverHint: string;
}) {
  return (
    <HoverHintContent hoverHint={hoverHint}>
      <span>{label}</span>
    </HoverHintContent>
  );
}

function isolateRowSelect(event: MouseEvent): void {
  event.stopPropagation();
}

function ShipmentLocationCell({ row }: { row: SuntronicInvoiceShipmentRow }): ReactNode {
  const locationId = resolveSuntronicShipmentLocationId(row.location_id, row.location_name);
  const fallbackLabel = row.location_name?.trim() || undefined;
  if (locationId != null) {
    return <ManufacturingLocationChip locationId={locationId} fallbackLabel={fallbackLabel} />;
  }
  if (fallbackLabel != null) {
    return <ManufacturingLocationChip locationId={0} fallbackLabel={fallbackLabel} />;
  }
  return "—";
}

function ShipmentSkuCell({
  sku,
  skuHint,
  skuHintsLoading,
  onSkuHoverIntent,
}: {
  sku: string;
  skuHint: MrpSkuHoverHintFields | undefined;
  skuHintsLoading: boolean;
  onSkuHoverIntent: () => void;
}): ReactNode {
  if (sku === "") return "—";
  return (
    <DetailLinkWithEntityPeek
      peek={{ kind: "sku_catalog", sku, hint: skuHint, loading: skuHintsLoading }}
      trigger={
        <Link
          to={buildMrpSkuDetailPath(sku, { tab: "inventory" })}
          className={ENTITY_LINK_CLASS}
          onPointerEnter={onSkuHoverIntent}
          onFocus={onSkuHoverIntent}
        >
          {sku}
        </Link>
      }
    />
  );
}

function ShipmentRow({
  row,
  selected,
  onSelect,
  skuHint,
  skuHintsLoading,
  onSkuHoverIntent,
  batchPeek,
  batchPeekLoading,
}: {
  row: SuntronicInvoiceShipmentRow;
  selected: boolean;
  onSelect: () => void;
  skuHint: MrpSkuHoverHintFields | undefined;
  skuHintsLoading: boolean;
  onSkuHoverIntent: () => void;
  batchPeek: HubBatchPeekApi | undefined;
  batchPeekLoading: boolean;
}) {
  const batchId = row.batch_id == null ? null : Number(row.batch_id);
  const shipmentId = Number(row.shipment_id);
  const sku = row.sku?.trim() ?? "";
  const peek = batchPeekFromApi(batchPeek, batchPeekLoading);

  return (
    <tr className={cn("cursor-pointer", selected ? "bg-sky-50" : "hover:bg-slate-50")} onClick={onSelect}>
      <Td>
        {batchId != null && Number.isFinite(batchId) && batchId > 0 ? (
          <span onClick={isolateRowSelect}>
            <BatchDetailLink batchId={batchId} className={cn(ENTITY_LINK_CLASS, "tabular-nums")} peek={peek}>
              {batchId}
            </BatchDetailLink>
          </span>
        ) : (
          "—"
        )}
      </Td>
      <Td>
        {Number.isFinite(shipmentId) && shipmentId > 0 ? (
          <span onClick={isolateRowSelect}>
            <ShipmentDetailLink
              shipmentId={shipmentId}
              className={cn(ENTITY_LINK_CLASS, "tabular-nums")}
              peek={{
                ship_date: row.date_shipped,
                ship_to_label: batchPeek?.ship_to_label ?? null,
              }}
            >
              {shipmentId}
            </ShipmentDetailLink>
          </span>
        ) : (
          "—"
        )}
      </Td>
      <Td>
        <ShipmentLocationCell row={row} />
      </Td>
      <Td>
        <span onClick={isolateRowSelect}>
          <ShipmentSkuCell
            sku={sku}
            skuHint={skuHint}
            skuHintsLoading={skuHintsLoading}
            onSkuHoverIntent={onSkuHoverIntent}
          />
        </span>
      </Td>
      <Td>{formatDate(row.date_shipped)}</Td>
      <Td className="text-right tabular-nums">{formatQuantity(row.shipment_quantity)}</Td>
      <Td className="text-right tabular-nums">{formatQuantity(row.total_matched_quantity ?? 0)}</Td>
      <Td className="text-right tabular-nums font-medium">{formatQuantity(row.remaining_quantity ?? 0)}</Td>
    </tr>
  );
}

export function SuntronicInvoiceShipmentsTable({
  rows,
  selectedShipmentId,
  selectedBatchItemId,
  onSelect,
  fillAvailableHeight = false,
}: {
  rows: readonly SuntronicInvoiceShipmentRow[];
  selectedShipmentId: number | null;
  selectedBatchItemId: number | null;
  onSelect: (row: SuntronicInvoiceShipmentRow) => void;
  fillAvailableHeight?: boolean;
}): JSX.Element {
  const [sortKey, setSortKey] = useState<SuntronicShipmentSortKey>("batch");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const toggleSort = (key: SuntronicShipmentSortKey): void => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "batch" || key === "sku" || key === "location" ? "asc" : "desc");
  };

  const sortedRows = useMemo(
    () => [...rows].sort((left, right) => compareSuntronicInvoiceShipments(left, right, sortKey, sortDir)),
    [rows, sortDir, sortKey],
  );

  const shipmentSkus = rows.map((row) => row.sku);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    shipmentSkus,
    "suntronic-invoice-shipments",
  );

  const batchIdsForPeek = useMemo(() => {
    const ids = new Set<number>();
    for (const row of rows) {
      const batchId = row.batch_id == null ? Number.NaN : Number(row.batch_id);
      if (Number.isFinite(batchId) && batchId > 0) ids.add(batchId);
    }
    return Array.from(ids).sort((left, right) => left - right);
  }, [rows]);

  const batchPeekQuery = useQuery<{ peeks: Record<number, HubBatchPeekApi> }, ApiError>({
    queryKey: ["manufacturing", "batch-peeks", "suntronic-invoice-shipments", batchIdsForPeek.join(",")],
    queryFn: () =>
      apiFetch<{ peeks: Record<number, HubBatchPeekApi> }>("/api/manufacturing/batch-peeks", {
        query: { ids: batchIdsForPeek.join(",") },
      }),
    enabled: batchIdsForPeek.length > 0,
    staleTime: 60_000,
    retry: false,
  });

  if (rows.length === 0) {
    return <p className="px-1 py-3 text-sm text-slate-600">No shipments match these filters.</p>;
  }

  return (
    <div
      className={
        fillAvailableHeight
          ? "min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200"
          : "max-h-[min(28rem,50vh)] overflow-auto rounded-lg border border-slate-200"
      }
    >
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500 backdrop-blur-sm">
          <tr>
            <SortableTableHeader label="Batch" sortKey="batch" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <SortableTableHeader
              label="Shipment"
              sortKey="shipment"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
            />
            <SortableTableHeader
              label="Location"
              sortKey="location"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
            />
            <SortableTableHeader label="SKU" sortKey="sku" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
            <SortableTableHeader
              label="Date shipped"
              sortKey="dateShipped"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
            />
            <SortableTableHeader
              label="Shipped"
              sortKey="shipmentQty"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
              align="right"
            />
            <SortableTableHeader
              label={<HeaderHintLabel label="Matched" hoverHint={SUNTRONIC_SHIPMENTS_MATCHED_QTY_HOVER_HINT} />}
              sortKey="matchedQty"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
              align="right"
            />
            <SortableTableHeader
              label={<HeaderHintLabel label="Remaining" hoverHint={SUNTRONIC_SHIPMENTS_REMAINING_QTY_HOVER_HINT} />}
              sortKey="remainingQty"
              activeKey={sortKey}
              dir={sortDir}
              onSort={toggleSort}
              align="right"
            />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sortedRows.map((row) => {
            const batchId = row.batch_id == null ? Number.NaN : Number(row.batch_id);
            return (
              <ShipmentRow
                key={`${row.shipment_id}-${row.batch_item_id}`}
                row={row}
                selected={row.shipment_id === selectedShipmentId && row.batch_item_id === selectedBatchItemId}
                onSelect={() => onSelect(row)}
                skuHint={skuHoverHints?.[row.sku.trim()]}
                skuHintsLoading={hintsLoading}
                onSkuHoverIntent={enableHintsFetch}
                batchPeek={Number.isFinite(batchId) ? lookupBatchPeek(batchPeekQuery.data?.peeks, batchId) : undefined}
                batchPeekLoading={batchPeekQuery.isPending || batchPeekQuery.isFetching}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
